import { Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import {
    dateManipulationCheckFailed,
    dateManipulationCheckLoaded,
    externalTimestampPersisted,
    externalTimestampPersistError,
    initDateManipulationCheck,
} from './actions/date-manipulation-check.actions';

/**
 * Dispatches `initDateManipulationCheck` and returns a **cold** observable that settles once the
 * init chain reports its outcome: completes on `dateManipulationCheckLoaded`, errors with
 * `new Error(action.error)` on `dateManipulationCheckFailed`.
 *
 * The outcome subscription is set up *before* dispatching, so a synchronously flushed effect
 * result cannot be missed — mirroring `dispatchRecipientsWrite`.
 *
 * Lives here, as a plain exported function rather than a service method, so
 * `DateManipulationCheckNgrxService` has zero private helpers beyond `assertInitialized`;
 * deleting the service later loses nothing.
 *
 * @param store the root store to dispatch the init request on
 * @param actions$ the actions stream to observe the settle outcome on
 * @returns an observable that completes on `dateManipulationCheckLoaded` and errors on
 * `dateManipulationCheckFailed`
 */
export function dispatchDateManipulationCheckInit(store: Store, actions$: Actions): Observable<unknown> {
    return new Observable((subscriber) => {
        const subscription = actions$
            .pipe(ofType(dateManipulationCheckLoaded, dateManipulationCheckFailed), take(1))
            .subscribe((action) => {
                if (action.type === dateManipulationCheckFailed.type) {
                    subscriber.error(new Error(action.error));
                    return;
                }
                subscriber.next(undefined);
                subscriber.complete();
            });
        store.dispatch(initDateManipulationCheck());
        return (): void => subscription.unsubscribe();
    });
}

/**
 * Dispatches an external-timestamp write request and returns a **cold** observable that settles
 * once the matching `externalTimestampPersisted` / `externalTimestampPersistError` outcome
 * arrives — mirroring `dispatchRecipientsWrite`.
 *
 * The outcome subscription is set up *before* dispatching, so a synchronously flushed effect
 * result cannot be missed.
 *
 * @param store the root store to dispatch the write request on
 * @param actions$ the actions stream to observe the settle outcome on
 * @param request the write action to dispatch (`externalTimestampReceived`)
 * @param correlationId the id carried by `request` and echoed by the settle outcome
 * @returns an observable that completes on `externalTimestampPersisted` and errors on
 * `externalTimestampPersistError`
 */
export function dispatchExternalTimestampWrite(
    store: Store,
    actions$: Actions,
    request: Action,
    correlationId: string,
): Observable<void> {
    return new Observable((subscriber) => {
        const subscription = actions$
            .pipe(
                ofType(externalTimestampPersisted, externalTimestampPersistError),
                filter((action) => action.correlationId === correlationId),
                take(1),
            )
            .subscribe((action) => {
                if (action.type === externalTimestampPersistError.type) {
                    subscriber.error(new Error(action.error));
                    return;
                }
                subscriber.next(undefined);
                subscriber.complete();
            });
        store.dispatch(request);
        return (): void => subscription.unsubscribe();
    });
}
